// /opt/isp_erp/frontend/src/pages/admin/finanzas/sucursales/Sucursales.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { finanzasAPI } from '../../../../api/finanzas/finanzas.api';

const Sucursales = () => {
  const [sucursales, setSucursales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedSucursal, setSelectedSucursal] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    codigo: '',
    direccion: '',
    telefono: '',
    email: '',
    activo: true
  });
  const [error, setError] = useState('');

  useEffect(() => {
    cargarSucursales();
  }, []);

  const cargarSucursales = async () => {
    setLoading(true);
    try {
      const response = await finanzasAPI.getSucursales();
      setSucursales(response.data);
    } catch (err) {
      console.error('Error:', err);
      setError('Error al cargar sucursales');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (sucursal = null) => {
    if (sucursal) {
      setEditMode(true);
      setSelectedSucursal(sucursal);
      setFormData({
        nombre: sucursal.nombre,
        codigo: sucursal.codigo,
        direccion: sucursal.direccion || '',
        telefono: sucursal.telefono || '',
        email: sucursal.email || '',
        activo: sucursal.activo
      });
    } else {
      setEditMode(false);
      setSelectedSucursal(null);
      setFormData({
        nombre: '',
        codigo: '',
        direccion: '',
        telefono: '',
        email: '',
        activo: true
      });
    }
    setError('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({
      nombre: '',
      codigo: '',
      direccion: '',
      telefono: '',
      email: '',
      activo: true
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async () => {
    if (!formData.nombre || !formData.codigo) {
      setError('Nombre y código son requeridos');
      return;
    }

    try {
      if (editMode) {
        await finanzasAPI.actualizarSucursal(selectedSucursal.id, formData);
      } else {
        await finanzasAPI.crearSucursal(formData);
      }
      handleCloseDialog();
      cargarSucursales();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta sucursal?')) return;
    try {
      await finanzasAPI.eliminarSucursal(id);
      cargarSucursales();
    } catch (err) {
      alert('Error al eliminar');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          <BusinessIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Sucursales
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Nueva Sucursal
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell>Código</TableCell>
              <TableCell>Nombre</TableCell>
              <TableCell>Dirección</TableCell>
              <TableCell>Teléfono</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sucursales.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No hay sucursales registradas
                </TableCell>
              </TableRow>
            ) : (
              sucursales.map((s) => (
                <TableRow key={s.id} hover>
                  <TableCell><strong>{s.codigo}</strong></TableCell>
                  <TableCell>{s.nombre}</TableCell>
                  <TableCell>{s.direccion || '-'}</TableCell>
                  <TableCell>{s.telefono || '-'}</TableCell>
                  <TableCell>{s.email || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={s.activo ? 'Activo' : 'Inactivo'}
                      color={s.activo ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleOpenDialog(s)} title="Editar">
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(s.id)} title="Eliminar">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? 'Editar Sucursal' : 'Nueva Sucursal'}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Nombre"
              name="nombre"
              value={formData.nombre}
              onChange={handleChange}
              fullWidth
              required
            />
            <TextField
              label="Código"
              name="codigo"
              value={formData.codigo}
              onChange={handleChange}
              fullWidth
              required
              helperText="Ej: MATRIZ, SUC01, SUC02"
            />
            <TextField
              label="Dirección"
              name="direccion"
              value={formData.direccion}
              onChange={handleChange}
              fullWidth
              multiline
              rows={2}
            />
            <TextField
              label="Teléfono"
              name="telefono"
              value={formData.telefono}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              label="Email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              fullWidth
              type="email"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editMode ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Sucursales;
